import {useSelector} from "react-redux";

import {isEqual} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../../app/rp_reducer";
import {useHitSearchSubmit} from "../../../search/hooks/use_hit_search_submit";
import {useSearchFormSubmit} from "../../../search/hooks/use_search_form_submit";
import {ISearchFormValues} from "../../../search/types/ISearchFormValues";
import {SearchFormValuesType} from "../../../search/types/SearchFormValuesType";
import {useAppSelector} from "../../../store/hooks";
import {IOfferModalQueryParams, OfferModal} from "../../components/offer_modal/OfferModal";
import {useOfferModalOffers} from "../../hooks/use_offer_modal_offers";
import {IOfferModalName} from "../../types/OfferModalName";
import {IOfferModalState} from "../../types/OfferModalState";

const modalName: IOfferModalName = "mainOfferModal";

export function DefaultOfferListingModal() {
    const page = useAppSelector((state) => state.offerModals[modalName].listingPage);
    const pageSize = useAppSelector((state) => state.offerModals[modalName].listingPageSize);
    const isOpen = useAppSelector((state) => state.offerModals[modalName].isOpen);
    const modalListingPage = useSelector((state: IRPStore) => state.offerList.list.page);
    const modalListingPageSize = useSelector((state: IRPStore) => state.offerList.list.page_size);
    const privilegedOffers = useSelector((state: IRPStore) => state.offerList.list.privileged_offers);

    const modalQueryParams: IOfferModalQueryParams = {
        listingPage: modalListingPage,
        listingPageSize: modalListingPageSize
    };

    const listingQueryParams = useSelector((state: IRPStore) => state.offerList.list.latestQuery);
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues);
    const modalQuery = {...listingQueryParams, page_size: pageSize, page: page};
    const defaultModalListingQuery = useOfferModalOffers(!isOpen, modalQuery, privilegedOffers);
    const hitSearchSubmit = useHitSearchSubmit();

    const searchFormSubmit = useSearchFormSubmit({
        multiRegionLink: rpAppLink.offer.multiList,
        latestQuery: page > 1 ? {...listingQueryParams, page} : listingQueryParams,
        listFriendlyLink: ({friendlySlug, offerListSubFilterSlug}) => {
            if (offerListSubFilterSlug) {
                return rpAppLink.offer.listFriendlySubFilterGeneral({friendlySlug, offerListSubFilter: offerListSubFilterSlug});
            }

            return rpAppLink.offer.listFriendly({friendlySlug});
        },
        abroadListFriendlyLink: (params) => {
            return rpAppLink.offer.listFriendlyAbroad(params);
        },
        onSearchSubmit: (formValues) => {
            hitSearchSubmit(formValues);
        }
    });

    return (
        <OfferModal
            modalName={modalName}
            offersQuery={defaultModalListingQuery}
            initialQueryParams={modalQueryParams}
            onClose={(modalConfig) => {
                // Only submit if filters have actually changed to avoid unnecessary navigation and scroll reset
                if (!haveFiltersChanged(searchFormValues, modalConfig.filters)) {
                    return;
                }

                searchFormSubmit({
                    ...(searchFormValues as SearchFormValuesType),
                    floor_choices: modalConfig.filters.floor_choices,
                    house_storeys: modalConfig.filters.house_storeys,
                    area: modalConfig.filters.area,
                    rooms: modalConfig.filters.rooms
                });
            }}
        />
    );
}

/**
 * Compares modal filters with search form values to determine if filters have changed.
 * Returns true if any filter has been modified, false otherwise.
 */
function haveFiltersChanged(searchFormValues: Partial<ISearchFormValues>, modalFilters: IOfferModalState["filters"]): boolean {
    // Compare floor_choices arrays
    // Ensure floor_choices is always an array (it can be a string, number, or array)
    let originalFloorChoices: number[] = [];

    if (Array.isArray(searchFormValues.floor_choices)) {
        originalFloorChoices = searchFormValues.floor_choices;
    } else if (searchFormValues.floor_choices != null) {
        originalFloorChoices = [searchFormValues.floor_choices];
    }

    const modalFloorChoices = modalFilters.floor_choices ?? [];

    if (!isEqual(originalFloorChoices.slice().sort(), modalFloorChoices.slice().sort())) {
        return true;
    }

    let originalHouseStoreys: number[] = [];

    if (Array.isArray(searchFormValues.house_storeys)) {
        originalHouseStoreys = searchFormValues.house_storeys;
    } else if (searchFormValues.house_storeys != null) {
        originalHouseStoreys = [searchFormValues.house_storeys];
    }

    const modalHouseStoreys = modalFilters.house_storeys ?? [];

    if (!isEqual(originalHouseStoreys.slice().sort(), modalHouseStoreys.slice().sort())) {
        return true;
    }

    // Compare area range
    const originalArea = searchFormValues.area ?? {lower: "", upper: ""};
    const modalArea = modalFilters.area ?? {lower: "", upper: ""};
    if (!isEqual(originalArea, modalArea)) {
        return true;
    }

    // Compare rooms range
    const originalRooms = searchFormValues.rooms ?? {lower: "", upper: ""};
    const modalRooms = modalFilters.rooms ?? {lower: "", upper: ""};
    if (!isEqual(originalRooms, modalRooms)) {
        return true;
    }

    return false;
}
